// Wizard guidato multi-step per configurare l'integrazione Outlook Calendar
import { useState, useCallback } from 'react'
import { useMutation } from '@tanstack/react-query'
import {
  ExternalLink,
  Copy,
  Check,
  Eye,
  EyeOff,
  AlertTriangle,
  Info,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
} from 'lucide-react'
import { Button } from '../../../components/ui/Button'
import api from '../../../lib/api'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface OutlookSetupWizardProps {
  onClose: () => void
  onConnected?: () => void
}

interface CredentialsForm {
  clientId: string
  tenantId: string
  clientSecret: string
}

type CopiedField = 'redirectUri' | null

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TOTAL_STEPS = 6

const REDIRECT_URI = `${window.location.protocol}//${window.location.hostname}:3001/api/integrations/calendar/outlook/callback`

const UUID_PLACEHOLDER = 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface StepIndicatorProps {
  current: number
  total: number
}

function StepIndicator({ current, total }: StepIndicatorProps) {
  return (
    <div className="flex items-center justify-center gap-2 mb-6" role="progressbar" aria-valuenow={current} aria-valuemin={1} aria-valuemax={total} aria-label={`Step ${current} di ${total}`}>
      {Array.from({ length: total }, (_, i) => {
        const step = i + 1
        const isCompleted = step < current
        const isCurrent = step === current

        return (
          <div key={step} className="flex items-center gap-2">
            <div
              className={[
                'flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold transition-all',
                isCompleted
                  ? 'bg-[var(--fc-primary)] text-white'
                  : isCurrent
                  ? 'border-2 border-[var(--fc-primary)] bg-[var(--fc-background)] text-[var(--fc-primary)]'
                  : 'border-2 border-[var(--fc-border)] bg-[var(--fc-background)] text-[var(--fc-foreground-muted)]',
              ].join(' ')}
              aria-label={isCompleted ? `Step ${step} completato` : isCurrent ? `Step ${step} corrente` : `Step ${step}`}
            >
              {isCompleted ? <Check className="h-3.5 w-3.5" aria-hidden="true" /> : step}
            </div>
            {step < total && (
              <div
                className={[
                  'h-px w-6 transition-all',
                  isCompleted ? 'bg-[var(--fc-primary)]' : 'bg-[var(--fc-border)]',
                ].join(' ')}
                aria-hidden="true"
              />
            )}
          </div>
        )
      })}
    </div>
  )
}

interface InfoBoxProps {
  variant: 'info' | 'warning'
  children: React.ReactNode
}

function InfoBox({ variant, children }: InfoBoxProps) {
  const isWarning = variant === 'warning'
  return (
    <div
      className={[
        'flex gap-3 rounded-md border p-3 text-sm',
        isWarning
          ? 'border-[var(--fc-warning)] bg-[color-mix(in_srgb,var(--fc-warning)_8%,transparent)] text-[var(--fc-foreground)]'
          : 'border-[var(--fc-border)] bg-[var(--fc-surface)] text-[var(--fc-foreground-muted)]',
      ].join(' ')}
      role={isWarning ? 'alert' : undefined}
    >
      {isWarning ? (
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[var(--fc-warning)]" aria-hidden="true" />
      ) : (
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-[var(--fc-primary)]" aria-hidden="true" />
      )}
      <div>{children}</div>
    </div>
  )
}

interface CopyBoxProps {
  value: string
  fieldKey: CopiedField
  copiedField: CopiedField
  onCopy: (value: string, field: CopiedField) => void
}

function CopyBox({ value, fieldKey, copiedField, onCopy }: CopyBoxProps) {
  const isCopied = copiedField === fieldKey
  return (
    <div className="flex items-center gap-2 rounded-md border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2">
      <code className="flex-1 break-all text-xs text-[var(--fc-foreground)]">{value}</code>
      <button
        type="button"
        onClick={() => onCopy(value, fieldKey)}
        className="shrink-0 rounded p-1 text-[var(--fc-foreground-muted)] transition-colors hover:bg-[var(--fc-surface-overlay)] hover:text-[var(--fc-foreground)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--fc-primary)]"
        aria-label={isCopied ? 'Copiato!' : 'Copia negli appunti'}
        title={isCopied ? 'Copiato!' : 'Copia negli appunti'}
      >
        {isCopied ? (
          <Check className="h-4 w-4 text-[var(--fc-success)]" aria-hidden="true" />
        ) : (
          <Copy className="h-4 w-4" aria-hidden="true" />
        )}
      </button>
    </div>
  )
}

interface NumberedListProps {
  items: React.ReactNode[]
}

function NumberedList({ items }: NumberedListProps) {
  return (
    <ol className="space-y-2">
      {items.map((item, idx) => (
        <li key={idx} className="flex gap-3">
          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[var(--fc-surface)] border border-[var(--fc-border)] text-xs font-semibold text-[var(--fc-foreground-muted)]">
            {idx + 1}
          </span>
          <span className="text-sm text-[var(--fc-foreground)]">{item}</span>
        </li>
      ))}
    </ol>
  )
}

// ---------------------------------------------------------------------------
// Steps
// ---------------------------------------------------------------------------

function Step1Intro() {
  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-[var(--fc-foreground)]">Collega Outlook Calendar</h2>
        <p className="mt-2 text-sm text-[var(--fc-foreground-muted)]">
          Questo wizard ti guiderà in 5 minuti a collegare il calendario Outlook (Microsoft 365) a KleisNote.
          Avrai bisogno di accesso al portale Azure.
        </p>
      </div>

      <div className="rounded-md border border-[var(--fc-border)] bg-[var(--fc-surface)] p-4">
        <p className="mb-3 text-sm font-medium text-[var(--fc-foreground)]">Prerequisiti</p>
        <ul className="space-y-2">
          {[
            'Account Microsoft 365 con accesso al portale Azure',
            'Permesso per registrare applicazioni in Azure AD',
          ].map((item) => (
            <li key={item} className="flex items-start gap-2 text-sm text-[var(--fc-foreground-muted)]">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[var(--fc-primary)]" aria-hidden="true" />
              {item}
            </li>
          ))}
        </ul>
      </div>

      <InfoBox variant="info">
        Il wizard non richiede di modificare variabili d'ambiente — le credenziali vengono salvate nel database di KleisNote.
      </InfoBox>
    </div>
  )
}

function Step2AppRegistration() {
  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-[var(--fc-foreground)]">Crea una App Registration su Azure</h2>
        <p className="mt-1 text-sm text-[var(--fc-foreground-muted)]">Segui questi passaggi nel portale Azure:</p>
      </div>

      <NumberedList
        items={[
          <>
            Vai su{' '}
            <a
              href="https://portal.azure.com"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-[var(--fc-primary)] underline underline-offset-2 hover:opacity-80"
            >
              portal.azure.com
            </a>
            {' '}→ <strong>"Azure Active Directory"</strong> → <strong>"App registrations"</strong>
          </>,
          <>Clicca <strong>"New registration"</strong></>,
          <>
            Nome: inserisci <code className="rounded bg-[var(--fc-surface)] border border-[var(--fc-border)] px-1.5 py-0.5 text-xs">KleisNote</code>
          </>,
          <>
            Supported account types: seleziona{' '}
            <strong>"Accounts in this organizational directory only"</strong>{' '}
            (o "Multitenant" se vuoi più tenant)
          </>,
          <>Redirect URI: lasciala vuota per ora (la configuri nel prossimo step)</>,
          <>Clicca <strong>"Register"</strong></>,
        ]}
      />

      <a
        href="https://portal.azure.com/#view/Microsoft_AAD_RegisteredApps/CreateApplicationBlade"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 rounded-md border border-[var(--fc-border)] bg-[var(--fc-surface)] px-4 py-2 text-sm font-medium text-[var(--fc-foreground)] transition-colors hover:bg-[var(--fc-surface-overlay)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--fc-primary)]"
      >
        <ExternalLink className="h-4 w-4" aria-hidden="true" />
        Apri Azure Portal
      </a>

      <InfoBox variant="info">
        Dopo la registrazione, resterai sulla pagina dell'app. Tienila aperta per i passaggi successivi.
      </InfoBox>
    </div>
  )
}

interface Step3Props {
  copiedField: CopiedField
  onCopy: (value: string, field: CopiedField) => void
}

function Step3RedirectUri({ copiedField, onCopy }: Step3Props) {
  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-[var(--fc-foreground)]">Configura il Redirect URI</h2>
        <p className="mt-1 text-sm text-[var(--fc-foreground-muted)]">
          Copia l'URI qui sotto e incollalo nella configurazione dell'app su Azure.
        </p>
      </div>

      <div>
        <p className="mb-2 text-sm font-medium text-[var(--fc-foreground)]">Redirect URI da usare:</p>
        <CopyBox value={REDIRECT_URI} fieldKey="redirectUri" copiedField={copiedField} onCopy={onCopy} />
      </div>

      <NumberedList
        items={[
          <>Nella pagina dell'app su Azure, clicca <strong>"Authentication"</strong> nel menu a sinistra</>,
          <>Clicca <strong>"Add a platform"</strong> → seleziona <strong>"Web"</strong></>,
          <>Incolla l'URI di redirect copiato sopra nel campo <strong>"Redirect URIs"</strong></>,
          <>Clicca <strong>"Configure"</strong></>,
        ]}
      />
    </div>
  )
}

interface Step4Props {
  form: CredentialsForm
  onChange: (field: keyof CredentialsForm, value: string) => void
}

function Step4ClientId({ form, onChange }: Step4Props) {
  const [usedCommon, setUsedCommon] = useState(form.tenantId === 'common')

  const handleCommonToggle = (checked: boolean) => {
    setUsedCommon(checked)
    if (checked) {
      onChange('tenantId', 'common')
    } else {
      onChange('tenantId', '')
    }
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-[var(--fc-foreground)]">Copia Application (client) ID</h2>
        <p className="mt-1 text-sm text-[var(--fc-foreground-muted)]">
          Nella pagina <strong>"Overview"</strong> dell'app, copia questi valori e incollali qui sotto:
        </p>
      </div>

      <div className="space-y-4">
        {/* Client ID */}
        <div>
          <label htmlFor="clientId" className="mb-1.5 block text-sm font-medium text-[var(--fc-foreground)]">
            Application (client) ID
            <span className="ml-1 text-[var(--fc-danger)]" aria-hidden="true">*</span>
          </label>
          <input
            id="clientId"
            type="text"
            value={form.clientId}
            onChange={(e) => onChange('clientId', e.target.value.trim())}
            placeholder={UUID_PLACEHOLDER}
            autoComplete="off"
            spellCheck={false}
            className="w-full rounded-md border border-[var(--fc-border)] bg-[var(--fc-background)] px-3 py-2 text-sm font-mono text-[var(--fc-foreground)] placeholder:text-[var(--fc-foreground-muted)] focus:border-[var(--fc-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--fc-primary)]"
          />
        </div>

        {/* Tenant ID */}
        <div>
          <div className="mb-1.5 flex items-center justify-between gap-3">
            <label htmlFor="tenantId" className="text-sm font-medium text-[var(--fc-foreground)]">
              Directory (tenant) ID
              <span className="ml-1 text-[var(--fc-danger)]" aria-hidden="true">*</span>
            </label>
            <label className="flex cursor-pointer items-center gap-1.5 text-xs text-[var(--fc-foreground-muted)]">
              <input
                type="checkbox"
                checked={usedCommon}
                onChange={(e) => handleCommonToggle(e.target.checked)}
                className="h-3.5 w-3.5 rounded border-[var(--fc-border)] accent-[var(--fc-primary)]"
              />
              Usa <code className="rounded bg-[var(--fc-surface)] border border-[var(--fc-border)] px-1 text-xs">'common'</code>
            </label>
          </div>
          <input
            id="tenantId"
            type="text"
            value={form.tenantId}
            onChange={(e) => {
              const val = e.target.value.trim()
              onChange('tenantId', val)
              setUsedCommon(val === 'common')
            }}
            placeholder={usedCommon ? 'common' : UUID_PLACEHOLDER}
            autoComplete="off"
            spellCheck={false}
            disabled={usedCommon}
            className="w-full rounded-md border border-[var(--fc-border)] bg-[var(--fc-background)] px-3 py-2 text-sm font-mono text-[var(--fc-foreground)] placeholder:text-[var(--fc-foreground-muted)] focus:border-[var(--fc-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--fc-primary)] disabled:opacity-60"
          />
          <p className="mt-1 text-xs text-[var(--fc-foreground-muted)]">
            Lascia 'common' se vuoi accettare tutti i tenant Microsoft.
          </p>
        </div>
      </div>
    </div>
  )
}

interface Step5Props {
  form: CredentialsForm
  onChange: (field: keyof CredentialsForm, value: string) => void
}

function Step5ClientSecret({ form, onChange }: Step5Props) {
  const [showSecret, setShowSecret] = useState(false)

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-[var(--fc-foreground)]">Crea un Client Secret</h2>
        <p className="mt-1 text-sm text-[var(--fc-foreground-muted)]">
          Segui questi passaggi per generare il secret e incollalo qui sotto.
        </p>
      </div>

      <NumberedList
        items={[
          <>Nel menu a sinistra, clicca <strong>"Certificates &amp; secrets"</strong></>,
          <>Clicca <strong>"New client secret"</strong></>,
          <>
            Descrizione:{' '}
            <code className="rounded bg-[var(--fc-surface)] border border-[var(--fc-border)] px-1.5 py-0.5 text-xs">KleisNote</code>
          </>,
          <>Scadenza: scegli <strong>"24 months"</strong> (o più lungo)</>,
          <>Clicca <strong>"Add"</strong></>,
          <>
            <strong>IMPORTANTE:</strong> Copia subito il valore nel campo <strong>"Value"</strong> — non sarà più visibile.
          </>,
        ]}
      />

      <div>
        <label htmlFor="clientSecret" className="mb-1.5 block text-sm font-medium text-[var(--fc-foreground)]">
          Client Secret Value
          <span className="ml-1 text-[var(--fc-danger)]" aria-hidden="true">*</span>
        </label>
        <div className="relative">
          <input
            id="clientSecret"
            type={showSecret ? 'text' : 'password'}
            value={form.clientSecret}
            onChange={(e) => onChange('clientSecret', e.target.value.trim())}
            placeholder="Incolla qui il valore del secret"
            autoComplete="new-password"
            spellCheck={false}
            className="w-full rounded-md border border-[var(--fc-border)] bg-[var(--fc-background)] px-3 py-2 pr-10 text-sm font-mono text-[var(--fc-foreground)] placeholder:font-sans placeholder:text-[var(--fc-foreground-muted)] focus:border-[var(--fc-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--fc-primary)]"
          />
          <button
            type="button"
            onClick={() => setShowSecret((prev) => !prev)}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-[var(--fc-foreground-muted)] transition-colors hover:text-[var(--fc-foreground)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--fc-primary)]"
            aria-label={showSecret ? 'Nascondi secret' : 'Mostra secret'}
          >
            {showSecret ? <EyeOff className="h-4 w-4" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />}
          </button>
        </div>
      </div>

      <InfoBox variant="warning">
        Il valore del secret è visibile solo ora. Se esci dalla pagina senza copiarlo, dovrai crearne uno nuovo.
      </InfoBox>
    </div>
  )
}

interface Step6Props {
  form: CredentialsForm
  isSaving: boolean
  isSaved: boolean
  isStartingOAuth: boolean
  oauthOpened: boolean
  saveError: string | null
  oauthError: string | null
  onSave: () => void
  onConnect: () => void
}

function Step6SaveAndTest({
  form,
  isSaving,
  isSaved,
  isStartingOAuth,
  oauthOpened,
  saveError,
  oauthError,
  onSave,
  onConnect,
}: Step6Props) {
  const maskSecret = (val: string) => {
    if (!val) return '—'
    if (val.length <= 4) return '••••'
    return val.slice(0, 2) + '••••••••' + val.slice(-2)
  }

  const maskId = (val: string) => {
    if (!val) return '—'
    const parts = val.split('-')
    if (parts.length === 5) {
      return `${parts[0]}-****-****-****-${parts[4]}`
    }
    return val.slice(0, 8) + '-****-****-****-' + val.slice(-12)
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-[var(--fc-foreground)]">Salva e connetti</h2>
        <p className="mt-1 text-sm text-[var(--fc-foreground-muted)]">
          Verifica i dati inseriti e salva la configurazione per avviare il flusso OAuth.
        </p>
      </div>

      {/* Summary */}
      <div className="rounded-md border border-[var(--fc-border)] bg-[var(--fc-surface)] divide-y divide-[var(--fc-border)]">
        {[
          { label: 'Application (client) ID', value: maskId(form.clientId) },
          { label: 'Directory (tenant) ID', value: form.tenantId || '—' },
          { label: 'Client Secret', value: maskSecret(form.clientSecret) },
        ].map(({ label, value }) => (
          <div key={label} className="flex items-start justify-between gap-4 px-4 py-3">
            <span className="text-xs font-medium text-[var(--fc-foreground-muted)]">{label}</span>
            <span className="break-all text-right text-xs font-mono text-[var(--fc-foreground)]">{value}</span>
          </div>
        ))}
      </div>

      {/* Save error */}
      {saveError && (
        <div className="flex gap-2 rounded-md border border-[var(--fc-danger)] bg-[color-mix(in_srgb,var(--fc-danger)_8%,transparent)] p-3 text-sm text-[var(--fc-danger)]" role="alert">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          {saveError}
        </div>
      )}

      {/* Save button */}
      {!isSaved && (
        <Button
          variant="primary"
          size="md"
          isLoading={isSaving}
          onClick={onSave}
          className="w-full"
        >
          Salva configurazione
        </Button>
      )}

      {/* OAuth section — shown after save */}
      {isSaved && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-sm text-[var(--fc-success)] font-medium">
            <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
            Configurazione salvata con successo
          </div>

          {oauthOpened ? (
            <InfoBox variant="info">
              Una finestra si aprirà per autorizzare l'accesso al tuo calendario Outlook. Dopo l'autorizzazione torna qui.
            </InfoBox>
          ) : (
            <>
              <p className="text-sm text-[var(--fc-foreground-muted)]">
                Ora puoi avviare il flusso OAuth per collegare il tuo account Microsoft.
              </p>

              {oauthError && (
                <div className="flex gap-2 rounded-md border border-[var(--fc-danger)] bg-[color-mix(in_srgb,var(--fc-danger)_8%,transparent)] p-3 text-sm text-[var(--fc-danger)]" role="alert">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                  {oauthError}
                </div>
              )}

              <Button
                variant="primary"
                size="md"
                isLoading={isStartingOAuth}
                leftIcon={<ExternalLink className="h-4 w-4" aria-hidden="true" />}
                onClick={onConnect}
                className="w-full"
              >
                Connetti account
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function OutlookSetupWizard({ onClose, onConnected }: OutlookSetupWizardProps) {
  const [step, setStep] = useState(1)
  const [copiedField, setCopiedField] = useState<CopiedField>(null)
  const [form, setForm] = useState<CredentialsForm>({
    clientId: '',
    tenantId: '',
    clientSecret: '',
  })
  const [isSaved, setIsSaved] = useState(false)
  const [oauthOpened, setOauthOpened] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [oauthError, setOauthError] = useState<string | null>(null)
  const [isStartingOAuth, setIsStartingOAuth] = useState(false)

  // ---- Copy to clipboard ----
  const handleCopy = useCallback((value: string, field: CopiedField) => {
    void navigator.clipboard.writeText(value).then(() => {
      setCopiedField(field)
      setTimeout(() => setCopiedField(null), 2000)
    })
  }, [])

  // ---- Form field change ----
  const handleFieldChange = useCallback((field: keyof CredentialsForm, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }))
  }, [])

  // ---- Save credentials mutation ----
  const saveMutation = useMutation({
    mutationFn: async (data: CredentialsForm) => {
      await api.patch('/integrations/calendar/credentials', {
        provider: 'outlook',
        clientId: data.clientId,
        clientSecret: data.clientSecret,
        tenantId: data.tenantId,
      })
    },
    onSuccess: () => {
      setSaveError(null)
      setIsSaved(true)
    },
    onError: (err: unknown) => {
      const message =
        err instanceof Error
          ? err.message
          : 'Errore durante il salvataggio. Verifica i dati e riprova.'
      setSaveError(message)
    },
  })

  // ---- Start OAuth flow ----
  const handleConnect = async () => {
    setIsStartingOAuth(true)
    setOauthError(null)
    try {
      const res = await api.get<{ authUrl: string }>('/integrations/calendar/outlook/auth')
      window.open(res.data.authUrl, '_blank', 'width=600,height=700')
      setOauthOpened(true)
      onConnected?.()
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : "Impossibile avviare il flusso OAuth. Assicurati che le credenziali siano corrette."
      setOauthError(message)
    } finally {
      setIsStartingOAuth(false)
    }
  }

  // ---- Navigation guards ----
  const canProceed = (): boolean => {
    if (step === 4) return form.clientId.trim().length > 0 && form.tenantId.trim().length > 0
    if (step === 5) return form.clientSecret.trim().length > 0
    return true
  }

  const handleNext = () => {
    if (step < TOTAL_STEPS && canProceed()) setStep((s) => s + 1)
  }

  const handleBack = () => {
    if (step > 1) setStep((s) => s - 1)
  }

  // ---- Render current step content ----
  const renderStep = () => {
    switch (step) {
      case 1:
        return <Step1Intro />
      case 2:
        return <Step2AppRegistration />
      case 3:
        return <Step3RedirectUri copiedField={copiedField} onCopy={handleCopy} />
      case 4:
        return <Step4ClientId form={form} onChange={handleFieldChange} />
      case 5:
        return <Step5ClientSecret form={form} onChange={handleFieldChange} />
      case 6:
        return (
          <Step6SaveAndTest
            form={form}
            isSaving={saveMutation.isPending}
            isSaved={isSaved}
            isStartingOAuth={isStartingOAuth}
            oauthOpened={oauthOpened}
            saveError={saveError}
            oauthError={oauthError}
            onSave={() => saveMutation.mutate(form)}
            onConnect={handleConnect}
          />
        )
      default:
        return null
    }
  }

  const isLastStep = step === TOTAL_STEPS

  return (
    <div className="flex flex-col" style={{ minHeight: 420 }}>
      {/* Step indicator */}
      <StepIndicator current={step} total={TOTAL_STEPS} />

      {/* Step content */}
      <div className="flex-1">{renderStep()}</div>

      {/* Navigation footer */}
      <div className="mt-8 flex items-center justify-between border-t border-[var(--fc-border)] pt-4">
        <div>
          {step > 1 ? (
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<ArrowLeft className="h-4 w-4" aria-hidden="true" />}
              onClick={handleBack}
              disabled={saveMutation.isPending || isStartingOAuth}
            >
              Indietro
            </Button>
          ) : (
            <Button variant="ghost" size="sm" onClick={onClose}>
              Annulla
            </Button>
          )}
        </div>

        <div className="flex items-center gap-3">
          <span className="text-xs text-[var(--fc-foreground-muted)]">
            {step} / {TOTAL_STEPS}
          </span>

          {/* On the last step, navigation is handled inside the step itself */}
          {!isLastStep && (
            <Button
              variant="primary"
              size="sm"
              rightIcon={<ArrowRight className="h-4 w-4" aria-hidden="true" />}
              onClick={handleNext}
              disabled={!canProceed()}
            >
              {step === 1 ? 'Inizia' : 'Avanti'}
            </Button>
          )}

          {isLastStep && (
            <Button variant="ghost" size="sm" onClick={onClose}>
              Chiudi
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
